/**
 * MyAssociationsCard — Iter-27.21
 *
 * Affiche les associations sur lesquelles l'apprenant est explicitement
 * positionné (visibility.user_ids contient son id) avec :
 *   - Logo + nom + badge vert "TON ASSO"
 *   - Aperçu des paliers (3 max)
 *   - Bouton "Réviser le pitch" → /associations/{aid}
 *
 * Trois variantes via la prop `variant` :
 *   - "compact" (dashboard) : carte unique avec 1 ou 2 assos résumées
 *   - "full" (profil)        : section visible mettant tous les détails
 *
 * Cache silencieusement si l'apprenant n'a aucune asso "perso" (mode "all"
 * uniquement) — le composant n'a alors pas de raison d'apparaître.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Sparkles, ArrowRight, Banknote } from "lucide-react";
import api from "../lib/api";

export default function MyAssociationsCard({ variant = "compact" }) {
    const [items, setItems] = useState(null); // null = loading, [] = none

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/associations");
                const mine = data.filter((a) => a.is_my_asso);
                setItems(mine);
            } catch (e) {
                setItems([]);
            }
        })();
    }, []);

    if (items === null) {
        return (
            <div
                className={`rounded-3xl p-5 lg:p-6 glass-card animate-pulse ${
                    variant === "compact"
                        ? "col-span-12 lg:col-span-5"
                        : "col-span-12"
                }`}
                data-testid="my-assos-loading"
            >
                <div className="h-32" />
            </div>
        );
    }

    if (items.length === 0) return null;

    const isFull = variant === "full";

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="my-assos-card"
            className={`rounded-3xl p-5 lg:p-6 relative overflow-hidden border border-emerald-500/25 bg-gradient-to-br from-emerald-500/8 via-shariti-orange/4 to-transparent ${
                isFull ? "" : "col-span-12 lg:col-span-5 glass-card-hover"
            }`}
        >
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />

            <div className="relative z-10">
                <div className="flex items-center gap-2.5 mb-1">
                    <Heart className="w-4 h-4 text-emerald-300" fill="currentColor" />
                    <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-emerald-300">
                        Mon affectation terrain
                    </p>
                </div>
                <h3 className="text-xl lg:text-2xl font-extrabold text-white tracking-tight mb-1">
                    {items.length === 1
                        ? "Mon association assignée"
                        : `Mes ${items.length} associations assignées`}
                </h3>
                <p className="text-xs text-white/55 font-light mb-4 leading-relaxed max-w-2xl">
                    {items.length === 1
                        ? "Voici l'association sur laquelle ton manager t'a explicitement positionné·e. C'est ta mission terrain principale. "
                        : "Voici les associations sur lesquelles ton manager t'a explicitement positionné·e — tu peux switcher selon ta journée. "}
                    <Link
                        to="/associations"
                        className="text-emerald-300 hover:text-emerald-200 font-bold inline-flex items-center gap-1"
                    >
                        Voir aussi celles en accès libre <ArrowRight className="w-3 h-3" />
                    </Link>
                </p>

                <div className={`grid gap-3 ${items.length > 1 ? "sm:grid-cols-2" : ""}`}>
                    {items.slice(0, isFull ? 6 : 2).map((a) => (
                        <AssoMiniCard key={a.association_id} asso={a} isFull={isFull} />
                    ))}
                </div>

                {!isFull && items.length > 2 && (
                    <Link
                        to="/associations"
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-emerald-300 hover:text-emerald-200"
                    >
                        + {items.length - 2} autre{items.length - 2 > 1 ? "s" : ""} ·
                        Voir toutes
                        <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                )}
            </div>
        </motion.div>
    );
}

function AssoMiniCard({ asso, isFull }) {
    const previewLevels = (asso.gift_levels || []).slice(0, 3);
    const hasLevels = previewLevels.length > 0;

    return (
        <div
            className="rounded-2xl bg-white/4 hover:bg-white/6 border border-white/10 hover:border-emerald-500/30 transition-all p-3.5"
            data-testid={`my-asso-${asso.association_id}`}
        >
            <div className="flex items-start gap-3 mb-3">
                {asso.logo_url ? (
                    <div className="w-11 h-11 rounded-xl overflow-hidden bg-white/10 shrink-0 flex items-center justify-center p-1.5">
                        <img
                            src={asso.logo_url}
                            alt={asso.name}
                            className="w-full h-full object-contain"
                        />
                    </div>
                ) : (
                    <div className="w-11 h-11 rounded-xl bg-shariti-orange/15 shrink-0 flex items-center justify-center text-base font-extrabold text-shariti-orange">
                        {asso.name.slice(0, 2).toUpperCase()}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-white tracking-tight leading-tight mb-0.5">
                        {asso.name}
                    </p>
                    <span className="inline-flex items-center gap-1 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-2 py-0.5 text-[9px] font-extrabold tracking-wider uppercase text-emerald-300">
                        Ton asso
                    </span>
                </div>
            </div>

            {hasLevels && (
                <div className="space-y-1.5 mb-3">
                    <p className="text-[10px] font-thin tracking-[0.2em] uppercase text-white/40 flex items-center gap-1.5">
                        <Banknote className="w-3 h-3" />
                        Tes paliers
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {previewLevels.map((g, i) => (
                            <span
                                key={i}
                                className="inline-flex items-center text-[11px] font-bold bg-shariti-orange/10 text-shariti-orange-light border border-shariti-orange/20 rounded-md px-2 py-0.5"
                                title={g.impact}
                            >
                                {g.amount} €
                            </span>
                        ))}
                        {(asso.gift_levels || []).length > 3 && (
                            <span className="text-[11px] text-white/40 px-1">
                                +{asso.gift_levels.length - 3}
                            </span>
                        )}
                    </div>
                </div>
            )}

            <Link
                to={`/associations/${asso.association_id}`}
                data-testid={`my-asso-cta-${asso.association_id}`}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-shariti-orange-light hover:text-shariti-orange transition-colors"
            >
                <Sparkles className="w-3.5 h-3.5" />
                Réviser le pitch
                <ArrowRight className="w-3.5 h-3.5" />
            </Link>
        </div>
    );
}
